<template>
  <div class="seminar-registration-participant-fi">
    <div
      v-if="index === 1 && formLoading"
      class="seminar-registration-participant-fi__loading"
    >
      <loading-indicator
        variant="spinnerLocal"
        transparent
      />
    </div>

    <template v-else>
      <div class="seminar-registration-participant-fi__title">
        <div class="seminar-registration-participant-fi__title__text">
          {{
            index === 1
              ? fields.registrationMainParticipantFormTitle?.value
              : fields.registrationParticipantFormTitle?.value?.replace('{0}', String(index))
          }}
        </div>
        <icon-fi
          v-if="index > 1"
          icon="trash"
          class="seminar-registration-participant-fi__title__delete"
          @click="seminarRegistrationStore.removeParticipant(index - 1)"
        />
      </div>

      <div class="seminar-registration-participant-fi__form">
        <template v-if="index === 1">
          <div class="seminar-registration-participant-fi__form__col">
            <select-input-fi
              v-if="salutationList.length"
              :value="participant.salutation || null"
              :label="'Anrede'"
              :options="salutationList"
              disabled
              light
              data-test="salutation"
              :data-test-2="participant.salutation"
            />

            <text-input-fi
              ref="inputFirstname"
              v-model.trim="participant.firstname"
              :label="fields.firstNameLabel?.value"
              class="firstname"
              name="firstname"
              data-test="firstname"
              disabled
              light
            />

            <text-input-fi
              ref="inputLastname"
              v-model.trim="participant.lastname"
              :label="fields.lastNameLabel?.value"
              name="lastname"
              data-test="lastname"
              disabled
              light
            />

            <text-input-fi
              ref="inputEmail"
              v-model.trim="participant.email"
              :label="fields.emailLabel?.value"
              name="email"
              data-test="email"
              light
              disabled
            />

            <text-input-fi
              v-model.trim="participant.phone"
              :label="fields.phoneLabel?.value"
              name="phone"
              data-test="phone"
              light
              disabled
            />
          </div>

          <div class="seminar-registration-participant-fi__form__col">
            <select-input-fi
              :value="participant.title || null"
              :label="fields.titleLabel?.value"
              :options="titleList"
              :data-test-2="participant.title"
              data-test="title"
              light
              disabled
            />

            <text-input-fi
              v-model.trim="seminarRegistrationStore.company"
              :label="fields.companyNameLabel?.value"
              class="company"
              name="company"
              data-test="company"
              disabled
              light
            />

            <div class="street-number">
              <text-input-fi
                ref="inputStreet"
                v-model.trim="address.street"
                :label="fields.streetLabel?.value"
                class="street"
                name="street"
                data-test="street"
                disabled
                light
              />

              <text-input-fi
                ref="inputStreetNumber"
                v-model.trim="address.streetNumber"
                :label="fields.streetNumberLabel?.value"
                class="number"
                name="streetNumber"
                data-test="street-number"
                disabled
                light
              />
            </div>

            <div class="zip-city">
              <text-input-fi
                ref="inputZipcode"
                v-model.trim="address.zipcode"
                :label="fields.zipCodeLabel?.value"
                class="zip"
                name="zipcode"
                data-test="zipcode"
                disabled
                light
              />

              <text-input-fi
                ref="inputCity"
                v-model.trim="address.city"
                :label="fields.cityLabel?.value"
                class="city"
                name="city"
                data-test="city"
                disabled
                light
              />
            </div>
            <div
              v-if="countryList.length > 0"
              class="country"
            >
              <select-input-fi
                :label="fields.countryLabel?.value"
                :value="address.country || null"
                :options="countryList"
                disabled
                data-test="country"
                light
              />
            </div>
          </div>
        </template>

        <template v-else>
          <select-input-fi
            v-if="salutationList.length"
            :value="participant.salutation || null"
            :label="'Anrede'"
            :options="salutationList"
            light
            data-test="salutation"
            :data-test-2="participant.salutation"
            @input="salutationChanged"
          />

          <select-input-fi
            :value="participant.title || null"
            :label="fields.titleLabel?.value"
            :options="titleList"
            light
            data-test="title"
            :data-test-2="participant.title"
            @input="titleChanged"
          />

          <text-input-fi
            ref="inputFirstname"
            v-model.trim="participant.firstname"
            :label="fields.firstNameLabel?.value"
            :rules="rules.firstname"
            :maxlength="120"
            class="firstname"
            name="firstname"
            data-test="firstname"
            required
            light
            @update:model-value="validate"
          />

          <text-input-fi
            ref="inputLastname"
            v-model.trim="participant.lastname"
            :label="fields.lastNameLabel?.value"
            :maxlength="120"
            :rules="rules.lastname"
            name="lastname"
            data-test="lastname"
            required
            light
            @update:model-value="validate"
          />

          <text-input-fi
            ref="inputEmail"
            v-model.trim="participant.email"
            :label="fields.emailLabel?.value"
            :rules="
              hasInvalidCharacters || enableEmailValidation
                ? rules.email
                : dynamicRule(rules.email, enableEmailValidationDefault)
            "
            :maxlength="120"
            name="email"
            data-test="email"
            light
            required
            @keyup="(el) => validateCharacters(el, 'email')"
            @keydown="enableEmailValidationDefault = false"
            @blur="emailBlurred"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { IconFi, LoadingIndicator, SelectInputFi, TextInputFi } from 'atoms';
import { useSeminarRegistrationStore } from 'store/seminar-registration';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useValidators } from '@/composables/validators';
import GET_CUSTOMER_PROFILE from '@/graphql/getCustomerProfile.gql';
import GET_ORG_UNIT from '@/graphql/getOrgUnit.gql';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';
import { getSelectList } from '@/utils/fieldTransformations';

interface Fields {
  registrationMainParticipantFormTitle?: SingleLineTextField;
  registrationParticipantFormTitle?: SingleLineTextField;
  salutationList?: any;
  firstNameLabel?: SingleLineTextField;
  lastNameLabel?: SingleLineTextField;
  emailLabel?: SingleLineTextField;
  titleList?: any;
  titleLabel?: SingleLineTextField;
  companyNameLabel?: SingleLineTextField;
  streetLabel?: SingleLineTextField;
  streetNumberLabel?: SingleLineTextField;
  zipCodeLabel?: SingleLineTextField;
  cityLabel?: SingleLineTextField;
  countryList?: any;
  phoneLabel?: SingleLineTextField;
  countryLabel?: SingleLineTextField;
  registrationAddParticipantBtn?: SingleLineTextField;
}

type GetSelectListFields = Fields & { [key: string]: any };

defineOptions({
  name: 'SeminarRegistrationParticipantFi',
});

const fischer = useFischer();
const seminarRegistrationStore: any = useSeminarRegistrationStore();
const { rules, hasInvalidCharacters, dynamicRule, validateCharacters } = useValidators();

const fields = useFields<Fields>();

const props = defineProps<{
  index: number;
}>();

const enableEmailValidation = ref<boolean>(false);
const enableEmailValidationDefault = ref<boolean>(true);
const formLoading = ref<boolean>(true);

const inputFirstname = useTemplateRef<typeof TextInputFi>('inputFirstname');
const inputLastname = useTemplateRef<typeof TextInputFi>('inputLastname');
const inputEmail = useTemplateRef<typeof TextInputFi>('inputEmail');
const inputStreet = useTemplateRef<typeof TextInputFi>('inputStreet');
const inputStreetNumber = useTemplateRef<typeof TextInputFi>('inputStreetNumber');
const inputZipcode = useTemplateRef<typeof TextInputFi>('inputZipcode');
const inputCity = useTemplateRef<typeof TextInputFi>('inputCity');

const participant = computed(() => {
  return seminarRegistrationStore.participants[props.index - 1] || [];
});

const salutationList = computed(() => {
  const [, salutationList] = getSelectList(fields as GetSelectListFields, 'salutationList');
  return salutationList;
});

const titleList = computed(() => {
  const [, titleList] = getSelectList(fields as GetSelectListFields, 'titleList');
  return titleList;
});

const address = computed(() => {
  return seminarRegistrationStore.address;
});

const countryList = computed(() => {
  const [, countryList] = getSelectList(fields as GetSelectListFields, 'countryList');
  return countryList;
});

const orgUnitVariables = ref<any>({});
const { executeQuery: executeOrgUnit } = useQuery({
  query: GET_ORG_UNIT,
  variables: orgUnitVariables,
  pause: true,
  requestPolicy: 'network-only',
});

const loadOrgUnit = async (_doc: any, vars: any) => {
  orgUnitVariables.value = vars;
  const state = await executeOrgUnit();
  if (state.error.value) {
    console.error('could not load org unit', state.error.value);
    return null;
  }
  return state.data.value;
};

const { data: customerData, error: customerError } = useQuery({
  query: GET_CUSTOMER_PROFILE,
  variables: {
    baseSiteId: fischer.baseSiteId(),
    accessToken: fischer.user.accessToken(),
    userId: fischer.user.id(),
  },
  pause: computed(() => props.index !== 1),
  requestPolicy: 'network-only',
});

watch(customerData, async (data) => {
  if (!data) return;
  const customer = data?.getCustomerProfile;
  if (customer) {
    participant.value.id = customer.uid;
    participant.value.salutation = customer.contactAddress.salutationCode || '';
    participant.value.firstname = customer.firstName || customer.contactAddress.firstName || '';
    participant.value.lastname = customer.lastName || customer.contactAddress.lastName || '';
    participant.value.email = customer.uid;
    participant.value.phone = customer.contactAddress.phone || '';
    participant.value.title = customer.contactAddress.titleCode || '';
    participant.value.myfischer = true;

    if (customer.company?.uid) {
      const orgUnitResult = await loadOrgUnit(GET_ORG_UNIT, {
        baseSiteId: fischer.baseSiteId(),
        accessToken: fischer.user.accessToken(),
        userId: fischer.user.id(),
        orgUnitId: customer.company.uid,
      });

      const getOrgUnit = orgUnitResult?.getOrgUnit;
      if (getOrgUnit) {
        const companyAddress = getOrgUnit.addresses?.[0];
        seminarRegistrationStore.company = getOrgUnit.name || '';
        address.value.street = companyAddress?.streetName || '';
        address.value.streetNumber = companyAddress?.streetNumber || '';
        address.value.zipcode = companyAddress?.postalCode || '';
        address.value.city = companyAddress?.town || '';
        address.value.country = companyAddress?.country?.isocode || '';
      }
    }

    formLoading.value = false;
    validate();
  }
});

watch(customerError, (error) => {
  if (error) {
    const { graphQLErrors } = error;
    console.error('could not load customer profile', graphQLErrors);
  }
});

const salutationChanged = (value: string) => {
  participant.value.salutation = value;
};

const titleChanged = (value: string) => {
  participant.value.title = value;
};

const emailBlurred = async () => {
  if (participant.value.email !== '') {
    enableEmailValidation.value = true;
    await nextTick();
    validate();
  }
};

const validate = () => {
  participant.value.isValid = Boolean(
    participant.value.firstname !== '' &&
    !inputFirstname.value?.errorBucket?.length &&
    participant.value.lastname !== '' &&
    !inputLastname.value?.errorBucket?.length &&
    participant.value.email !== '' &&
    !inputEmail.value?.errorBucket?.length,
  );

  if (seminarRegistrationStore.totalParticipants === 1) {
    seminarRegistrationStore.formValid = true;
  } else {
    seminarRegistrationStore.formValid = Boolean(
      seminarRegistrationStore.company !== '' &&
      address.value.street !== '' &&
      !inputStreet.value?.errorBucket?.length &&
      address.value.streetNumber !== '' &&
      !inputStreetNumber.value?.errorBucket?.length &&
      address.value.zipcode !== '' &&
      !inputZipcode.value?.errorBucket?.length &&
      address.value.city !== '' &&
      !inputCity.value?.errorBucket?.length &&
      address.value.country !== '',
    );
  }
};
</script>

<style scoped src="./seminar-registration-participant-fi" />
